import {
    CategoryType,
    DishInMenuType,
    DishTranslation,
    DishType,
    MenuType,
} from '../../common/types/ResponseType';

export const SOURCE_LOCALE = 'en';

function pick(original: string, translated?: string): string {
    const next = translated?.trim();
    return next ? next : original;
}

export function supportedLocalesOf(menu: MenuType | null): string[] {
    if (!menu || !Array.isArray(menu.supportedLocales)) return [];
    return menu.supportedLocales.filter(
        (code): code is string => typeof code === 'string' && code !== SOURCE_LOCALE
    );
}

export function localizeMenuName(menu: MenuType, locale: string): string {
    if (locale === SOURCE_LOCALE) return menu.name;
    return pick(menu.name, menu.translations?.[locale]?.name);
}

export function localizeCategoryName(category: CategoryType, locale: string): string {
    if (locale === SOURCE_LOCALE) return category.name;
    return pick(category.name, category.translations?.[locale]?.name);
}

function localizeDishFields<T extends DishInMenuType | DishType>(dish: T, locale: string): T {
    if (locale === SOURCE_LOCALE) return dish;
    const t: DishTranslation | undefined = dish.translations?.[locale];
    if (!t) return dish;

    const ingredients =
        'ingredients' in dish && Array.isArray(dish.ingredients)
            ? dish.ingredients.map((ingredient) => {
                  const match = t.ingredients?.find((item) => item.id === ingredient.id);
                  return match?.name ? { ...ingredient, name: match.name } : ingredient;
              })
            : undefined;

    return {
        ...dish,
        name: pick(dish.name, t.name),
        description: pick(dish.description, t.description),
        cookingProccesDescription: pick(dish.cookingProccesDescription, t.cookingProccesDescription),
        ...('funFact' in dish
            ? { funFact: pick((dish as DishType).funFact ?? '', t.funFact) }
            : {}),
        ...(dish.category
            ? {
                  category: {
                      ...dish.category,
                      name: pick(dish.category.name, t.category ?? dish.category.translations?.[locale]?.name),
                  },
              }
            : {}),
        ...(ingredients ? { ingredients } : {}),
    };
}

export function localizeDish<T extends DishInMenuType | DishType>(dish: T, locale: string): T {
    return localizeDishFields(dish, locale);
}
